import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Users, Upload, Eye, ScrollText, ArrowLeft, X, Filter, Download,
  ShieldCheck, ChevronLeft, ChevronRight, RefreshCw, CheckCircle2,
} from "lucide-react";

/**
 * /dashboard/events/{event_id}/rbac
 * ----------------------------------------------------------------------
 * RBAC Phase B — Assignments + Bulk Import + Audit Viewer + View-as-Role
 * combined into a single tabbed admin surface so organisers have one
 * place to manage role membership end-to-end.
 */
const TABS = [
  { key: "assignments", label: "Assignments", icon: Users },
  { key: "bulk", label: "Bulk Import", icon: Upload },
  { key: "audit", label: "Audit Log", icon: ScrollText },
  { key: "view-as", label: "View as Role", icon: Eye },
];

export default function EventRBACPhaseBPage() {
  const { eventId } = useParams();
  const [active, setActive] = useState("assignments");

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="rbac-phase-b-page">
        <Link to="/dashboard/events"
              className="text-xs text-slate-500 hover:text-[#0F4C81] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to events
        </Link>
        <div className="mt-3 mb-6">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Advanced Event RBAC
          </p>
          <h1 className="mt-1 text-3xl font-bold text-[#0A192F] tracking-tight">
            Role Assignments &amp; Audit
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Assign users to event roles, import in bulk, review the
            audit trail, and preview the platform as any role.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200"
             data-testid="rbac-phase-b-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActive(t.key)}
                data-testid={`rbac-tab-${t.key}`}
                className={`relative px-4 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-all ${
                  isActive
                    ? "text-[#0A192F]"
                    : "text-slate-500 hover:text-[#0A192F]"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {isActive && (
                  <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#D4AF37]" />
                )}
              </button>
            );
          })}
        </div>

        {active === "assignments" && <AssignmentsTab eventId={eventId} />}
        {active === "bulk" && <BulkImportTab eventId={eventId} />}
        {active === "audit" && <AuditLogTab eventId={eventId} />}
        {active === "view-as" && <ViewAsTab eventId={eventId} />}
      </div>
    </DashboardLayout>
  );
}

// ===========================================================================
// 1. Assignments tab
// ===========================================================================
function AssignmentsTab({ eventId }) {
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ role_key: "", status: "active" });
  const [newAssignment, setNewAssignment] = useState({
    user_id: "", role_key: "",
  });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.role_key) params.set("role_key", filter.role_key);
      if (filter.status) params.set("status", filter.status);
      const [a, b] = await Promise.all([
        api.get(`/events/${eventId}/role-assignments?${params}`),
        api.get(`/events/${eventId}/role-assignments/stats`),
      ]);
      setAssignments(a.data?.assignments || []);
      setStats(b.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [eventId, filter]);

  useEffect(() => { load(); }, [load]);

  const createOne = async () => {
    if (!newAssignment.user_id || !newAssignment.role_key) {
      toast.error("user_id and role_key are required.");
      return;
    }
    setCreating(true);
    try {
      await api.post(`/events/${eventId}/role-assignments`, newAssignment);
      toast.success("Assignment created.");
      setNewAssignment({ user_id: "", role_key: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this role assignment?")) return;
    try {
      await api.delete(`/events/${eventId}/role-assignments/${id}?reason=revoked-from-ui`);
      toast.success("Assignment revoked.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="rbac-assignments-tab">
      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-4"
             data-testid="assignments-total-stat">
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            Active assignments
          </p>
          <p className="mt-2 text-3xl font-bold text-[#0A192F]">
            {stats?.total_active ?? 0}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
            By role
          </p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(stats?.by_role || {}).slice(0, 8).map(([role, count]) => (
              <span key={role}
                    className="text-[10px] px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-bold">
                {role}: {count}
              </span>
            ))}
            {!stats?.by_role && (
              <p className="text-xs text-slate-500">No assignments yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Create */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6"
           data-testid="assignment-create-panel">
        <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
          Quick assignment
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            placeholder="user_id"
            value={newAssignment.user_id}
            onChange={(e) => setNewAssignment({ ...newAssignment, user_id: e.target.value })}
            data-testid="assignment-userid-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono"
          />
          <input
            placeholder="role_key (e.g. committee_chair)"
            value={newAssignment.role_key}
            onChange={(e) => setNewAssignment({ ...newAssignment, role_key: e.target.value })}
            data-testid="assignment-rolekey-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono"
          />
          <button
            type="button"
            disabled={creating}
            onClick={createOne}
            data-testid="assignment-create-btn"
            className="bg-[#0A192F] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50] disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create assignment"}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Tip: use Bulk Import for more than a few assignments.
        </p>
      </div>

      {/* Filter + list */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-200">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <input
            value={filter.role_key}
            onChange={(e) => setFilter({ ...filter, role_key: e.target.value })}
            placeholder="Filter by role_key"
            data-testid="assignments-filter-rolekey"
            className="px-2 py-1 rounded border border-slate-200 text-xs font-mono"
          />
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            data-testid="assignments-filter-status"
            className="px-2 py-1 rounded border border-slate-200 text-xs bg-white"
          >
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
            <option value="">All</option>
          </select>
          <button onClick={load} className="ml-auto text-[11px] text-[#0F4C81] hover:underline inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {loading ? (
          <p className="px-5 py-6 text-sm text-slate-500">Loading…</p>
        ) : assignments.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">No assignments match these filters.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {assignments.map((a) => (
              <div key={a.id}
                   data-testid={`assignment-row-${a.id}`}
                   className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#0A192F]">
                    {a.user_name || a.user_email || a.user_id}
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                      {a.role_key}
                    </span>
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {a.source} · {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                    a.status === "active"
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-100 text-slate-500"
                  }`}>
                    {a.status}
                  </span>
                  {a.status === "active" && (
                    <button onClick={() => revoke(a.id)}
                            data-testid={`assignment-revoke-${a.id}`}
                            className="text-slate-400 hover:text-rose-600">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 2. Bulk Import tab
// ===========================================================================
function BulkImportTab({ eventId }) {
  const [file, setFile] = useState(null);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!eventId) return;
    try {
      const r = await api.get(`/events/${eventId}/role-assignments/imports`);
      setHistory(r.data?.imports || []);
    } catch (e) {
      // silent
    }
  }, [eventId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const upload = async () => {
    if (!file) {
      toast.error("Pick a CSV first.");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/events/${eventId}/role-assignments/bulk-import`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(r.data);
      if (r.data.status === "completed") {
        toast.success(`${r.data.succeeded_rows} assignments created.`);
      } else if (r.data.status === "completed_with_errors") {
        toast.warning(`${r.data.succeeded_rows} succeeded · ${r.data.failed_rows} failed.`);
      } else {
        toast.error("Bulk import failed. See errors below.");
      }
      await loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = () => {
    const sample = "email,role_key,committee_id,starts_at,ends_at\n"
      + "delegate1@example.com,committee_chair,,,\n"
      + "delegate2@example.com,rapporteur,UNSC,,\n";
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rbac-bulk-import-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="rbac-bulk-tab">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6"
           data-testid="bulk-import-panel">
        <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
          Upload CSV
        </p>
        <p className="text-sm text-slate-600 mb-4">
          Required columns: <code className="bg-slate-100 px-1 rounded">email</code>{" "}
          and <code className="bg-slate-100 px-1 rounded">role_key</code>. Optional:{" "}
          <code className="bg-slate-100 px-1 rounded">committee_id</code>,{" "}
          <code className="bg-slate-100 px-1 rounded">starts_at</code>,{" "}
          <code className="bg-slate-100 px-1 rounded">ends_at</code>. Max 5 MB / 5,000 rows.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file" accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="bulk-import-file"
            className="text-sm"
          />
          <button onClick={upload} disabled={uploading || !file}
                  data-testid="bulk-import-submit"
                  className="bg-[#0A192F] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50] disabled:opacity-50">
            {uploading ? "Uploading…" : "Upload & process"}
          </button>
          <button onClick={downloadSample}
                  data-testid="bulk-import-sample"
                  className="text-xs text-[#0F4C81] hover:underline inline-flex items-center gap-1">
            <Download className="h-3 w-3" /> Sample CSV
          </button>
        </div>
        {result && (
          <div className="mt-5 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm"
               data-testid="bulk-import-result">
            <p className="font-bold text-[#0A192F]">
              Import {result.status}: {result.succeeded_rows} succeeded ·{" "}
              {result.failed_rows} failed · {result.total_rows} total
              ({result.elapsed_ms} ms)
            </p>
            {result.sample_failures?.length > 0 && (
              <div className="mt-3 max-h-48 overflow-y-auto">
                <p className="text-xs uppercase tracking-wider text-rose-600 font-bold mb-1">
                  Sample failures
                </p>
                <ul className="text-xs space-y-1">
                  {result.sample_failures.map((f, i) => (
                    <li key={i} className="text-slate-700">
                      Row {f.row} ({f.email || "—"}): {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* History */}
      <div className="rounded-2xl border border-slate-200 bg-white"
           data-testid="bulk-import-history">
        <p className="px-5 py-4 text-xs uppercase tracking-[0.22em] text-slate-500 font-bold border-b border-slate-200">
          Recent imports
        </p>
        {history.length === 0 ? (
          <p className="px-5 py-4 text-sm text-slate-500">No imports yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {history.map((h) => (
              <div key={h.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#0A192F]">
                    {h.filename}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {h.succeeded_rows}/{h.total_rows} succeeded ·{" "}
                    {new Date(h.created_at).toLocaleString()}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                  h.status === "completed" ? "bg-emerald-50 text-emerald-700"
                    : h.status === "completed_with_errors" ? "bg-amber-50 text-amber-700"
                    : "bg-rose-50 text-rose-700"
                }`}>
                  {h.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 3. Audit Log tab
// ===========================================================================
function AuditLogTab({ eventId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState({ action: "", role_key: "" });
  const LIMIT = 50;

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.action) params.set("action", filter.action);
      if (filter.role_key) params.set("role_key", filter.role_key);
      params.set("limit", String(LIMIT));
      params.set("offset", String(offset));
      const r = await api.get(`/events/${eventId}/rbac/audit?${params}`);
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [eventId, filter, offset]);

  const loadActions = useCallback(async () => {
    if (!eventId) return;
    try {
      const r = await api.get(`/events/${eventId}/rbac/audit/actions`);
      setActions(r.data?.actions || []);
    } catch (e) {
      // silent
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadActions(); }, [loadActions]);

  return (
    <div data-testid="rbac-audit-tab">
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-200">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <select
            value={filter.action}
            onChange={(e) => { setOffset(0); setFilter({ ...filter, action: e.target.value }); }}
            data-testid="audit-filter-action"
            className="px-2 py-1 rounded border border-slate-200 text-xs bg-white"
          >
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input
            value={filter.role_key}
            onChange={(e) => setFilter({ ...filter, role_key: e.target.value })}
            onBlur={() => { setOffset(0); load(); }}
            placeholder="role_key"
            data-testid="audit-filter-rolekey"
            className="px-2 py-1 rounded border border-slate-200 text-xs font-mono"
          />
          <span className="text-[11px] text-slate-500">{total} total</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                    disabled={offset === 0}
                    data-testid="audit-prev-btn"
                    className="px-2 py-1 text-[11px] border border-slate-200 rounded disabled:opacity-40">
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="text-[11px] text-slate-500">
              {offset + 1}–{Math.min(offset + LIMIT, total)}
            </span>
            <button onClick={() => setOffset(offset + LIMIT)}
                    disabled={offset + LIMIT >= total}
                    data-testid="audit-next-btn"
                    className="px-2 py-1 text-[11px] border border-slate-200 rounded disabled:opacity-40">
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
        {loading ? (
          <p className="px-5 py-6 text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">No audit rows match these filters.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div key={r.id} data-testid={`audit-row-${r.id}`} className="px-5 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#0A192F]">
                    {r.action}
                    {r.role_key && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                        {r.role_key}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  by {r.actor_email || r.actor_id}
                  {r.reason ? ` · "${r.reason}"` : ""}
                </p>
                {(r.before || r.after) && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-[#0F4C81] cursor-pointer">View diff</summary>
                    <pre className="mt-2 p-2 bg-slate-50 rounded text-[10px] font-mono overflow-x-auto">
{JSON.stringify({ before: r.before, after: r.after }, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 4. View-as-Role tab
// ===========================================================================
function ViewAsTab({ eventId }) {
  const [targetRole, setTargetRole] = useState("delegate");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rbac/view-as/sessions");
      setSessions(r.data?.sessions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    if (!targetRole.trim()) return;
    setCreating(true);
    try {
      const r = await api.post("/rbac/view-as", {
        target_role_key: targetRole.trim(),
        target_event_id: eventId,
      });
      toast.success(`Preview token minted. Valid until ${new Date(r.data.expires_at).toLocaleTimeString()}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/rbac/view-as/sessions/${id}`);
      toast.success("Session revoked.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = (token) => {
    navigator.clipboard?.writeText(token);
    toast.success("Token copied. Paste it into X-View-As-Token header to preview.");
  };

  const ROLE_SUGGESTIONS = [
    "delegate", "chair", "committee_chair", "rapporteur",
    "faculty_advisor", "parent", "judge_adjudicator",
    "registration_desk", "media_team",
  ];

  return (
    <div data-testid="rbac-view-as-tab">
      <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 mb-6 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-900 leading-relaxed">
          Preview tokens are <strong>read-only</strong> and expire in 15 minutes.
          Every preview session is audit-logged. Use them to confirm what a
          delegate, chair or parent sees before broadcasting changes.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6"
           data-testid="view-as-create-panel">
        <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
          Start a preview
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
            placeholder="role_key"
            data-testid="view-as-role-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono"
          />
          <button onClick={start} disabled={creating}
                  data-testid="view-as-start-btn"
                  className="bg-[#0A192F] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50] disabled:opacity-50">
            {creating ? "Minting…" : "Start preview"}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {ROLE_SUGGESTIONS.map((r) => (
            <button key={r} type="button" onClick={() => setTargetRole(r)}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Sessions */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <p className="px-5 py-4 text-xs uppercase tracking-[0.22em] text-slate-500 font-bold border-b border-slate-200">
          My preview sessions
        </p>
        {loading ? (
          <p className="px-5 py-4 text-sm text-slate-500">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="px-5 py-4 text-sm text-slate-500">No sessions yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {sessions.map((s) => {
              const expired = !s.active || new Date(s.expires_at) < new Date();
              return (
                <div key={s.id} data-testid={`view-as-row-${s.id}`}
                     className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#0A192F]">
                      <span className="font-mono">{s.target_role_key}</span>
                      {s.target_event_id && (
                        <span className="ml-2 text-[10px] text-slate-400">
                          on {s.target_event_id.slice(-8)}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      Expires {new Date(s.expires_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                      expired ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"
                    }`}>
                      {expired ? "expired" : "active"}
                    </span>
                    {!expired && (
                      <>
                        <button onClick={() => copy(s.token)}
                                data-testid={`view-as-copy-${s.id}`}
                                className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-md bg-white border border-slate-200 hover:border-slate-300">
                          Copy token
                        </button>
                        <button onClick={() => revoke(s.id)}
                                data-testid={`view-as-revoke-${s.id}`}
                                className="text-slate-400 hover:text-rose-600">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
